import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close, Phone, WhatsApp } from "@mui/icons-material";
import type { DonationResponse } from "./DonationsTable";

interface DonorDetailDialogProps {
  open: boolean;
  donation: DonationResponse | null;
  onClose: () => void;
}

const getInitials = (name?: string | null) => {
  if (!name) return "?";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Admin-only drill-down for a single donation row -- name, contact info,
 * and quick Call/WhatsApp actions. Deliberately a separate component from
 * DonationsTable's own row rendering so it's easy to keep this off the
 * public memorial page (DonationTab.tsx), which reuses the same table. */
export default function DonorDetailDialog({
  open,
  donation,
  onClose,
}: DonorDetailDialogProps) {
  if (!donation) return null;

  const name =
    donation.display_name || donation.donor_name || "Anonymous donor";

  const handleCall = () => {
    if (donation.phone) window.location.href = `tel:${donation.phone}`;
  };

  const handleWhatsApp = () => {
    if (!donation.phone) return;
    const digits = donation.phone.replace(/[^\d+]/g, "");
    window.open(`https://wa.me/${digits.replace(/^\+/, "")}`, "_blank");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 56, height: 56, fontWeight: 700 }}>
              {getInitials(name)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700} fontSize={18} noWrap>
                {name}
              </Typography>
              {donation.is_anonymous && (
                <Chip
                  label="Requested anonymity publicly"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={handleCall}
                disabled={!donation.phone}
                sx={{ bgcolor: "action.hover" }}
              >
                <Phone fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleWhatsApp}
                disabled={!donation.phone}
                sx={{ bgcolor: "action.hover" }}
              >
                <WhatsApp fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {donation.email || "—"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {donation.phone || "—"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Reference
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {donation.reference || "—"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Amount
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {donation.currency === "ZWL" ? "ZWL" : "$"}
                {parseFloat(donation.amount || "0").toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Payment method
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {donation.payment_method || "—"}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
